// data-source-form.service.ts
import { Injectable } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';

@Injectable({ providedIn: 'root' })
export class DataSourceFormService {
  constructor(private readonly _fb: FormBuilder) {}

  // Build base form; caller can extend it if needed
  createEmptyForm(): FormGroup {
    return this._fb.group({
      name: ['', [Validators.required, Validators.maxLength(50)]],
      description: ['', [Validators.maxLength(5000)]],
      department: [[], Validators.required],
      type: ['', Validators.required],
      externalType: [''],
      internalType: [''],
      selectedTables: [[], []],
      manager: [''],
      joins: this._fb.array([]),
      agentWritable: [false],
    });
  }

  /**
   * Agents can only save files into document data sources, so "Writable by agent" is offered (and
   * sent as true) only while the form describes one.
   */
  supportsAgentWriting(form: FormGroup): boolean {
    return (
      form.get('type')?.value === 'internal' &&
      form.get('internalType')?.value === 'Unstructured Documents'
    );
  }

  createJoinControl(): FormControl {
    return this._fb.control('');
  }

  /**
   * Apply validators for internalType/externalType based on data source type.
   * When type is 'internal', only internalType is required.
   * When type is 'external', only externalType is required.
   */
  applyTypeDependentValidators(form: FormGroup, type: string): void {
    const internalCtrl = form.get('internalType');
    const externalCtrl = form.get('externalType');

    if (type === 'internal') {
      internalCtrl?.setValidators([Validators.required]);
      externalCtrl?.setValidators([]);
      externalCtrl?.setValue('', { emitEvent: false });
    } else if (type === 'external') {
      externalCtrl?.setValidators([Validators.required]);
      internalCtrl?.setValidators([]);
      internalCtrl?.setValue('', { emitEvent: false });
    } else {
      internalCtrl?.setValidators([]);
      externalCtrl?.setValidators([]);
    }

    internalCtrl?.updateValueAndValidity({ emitEvent: false });
    externalCtrl?.updateValueAndValidity({ emitEvent: false });
  }

  patchFromDataSource(form: FormGroup, dataSource: any, isAdmin: boolean, dialogType: string): void {
    if (!dataSource) return;

    form.patchValue({
      name: dataSource.name ?? '',
      description: dataSource.description ?? '',
      department: this.departmentIdsFromDataSource(dataSource),
      type: dataSource.type ?? '',
      internalType: dataSource.type === 'internal' ? dataSource.datatype : '',
      externalType: dataSource.type === 'external' ? dataSource.datatype : '',
      selectedTables: dataSource.external_tables ?? [],
      agentWritable: !!dataSource.agentWritable,
    });

    // Joins
    if (Array.isArray(dataSource.joins) && dataSource.joins.length > 0) {
      const joinsArray = form.get('joins') as FormArray;
      joinsArray.clear();
      dataSource.joins.forEach((join: string) => {
        joinsArray.push(this._fb.control(join ?? ''));
      });
    }

    // Manager field validators and value (only for Edit/View)
    if (dialogType === 'Edit' || dialogType === 'View') {
      const managerCtrl = form.get('manager');
      managerCtrl?.setValidators([Validators.required]);

      if (dataSource.ownedBy) {
        const ownedBy = dataSource.ownedBy;
        form.patchValue({
          manager: {
            id: ownedBy.id,
            role: this.normalizeOwnershipRole(ownedBy.role ?? ''),
          },
        });
      }
    }

    form.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Maps local / auth role names to API ownership roles:
   * `user` (admin), `member` (manager), `staff`.
   */
  normalizeOwnershipRole(role: string | null | undefined): string {
    const r = (role ?? '').trim().toLowerCase();
    if (r === 'manager' || r === 'member') {
      return 'member';
    }
    if (r === 'staff') {
      return 'staff';
    }
    if (r === 'company' || r === 'admin' || r === 'user') {
      return 'user';
    }
    return (role ?? '').trim();
  }

  /**
   * Maps the selected member to API ownership using backend `id` and `role`
   * (`user` | `member` | `staff`). Used on Add and Edit.
   */
  buildOwnership(managerValue: any, membersList?: any[]): { role: string; id: number } {
    if (managerValue != null && typeof managerValue === 'object' && !Array.isArray(managerValue)) {
      const id = Number(managerValue.id);
      const rawRole =
        managerValue.role != null && String(managerValue.role).trim() !== ''
          ? String(managerValue.role).trim()
          : '';
      return {
        role: this.normalizeOwnershipRole(rawRole),
        id: Number.isFinite(id) ? id : 0,
      };
    }

    const idNum =
      managerValue !== null && managerValue !== undefined && managerValue !== ''
        ? parseInt(String(managerValue), 10)
        : NaN;

    if (Number.isFinite(idNum) && membersList?.length) {
      const member = membersList.find(m => +m.id === idNum);
      if (member) {
        const rawRole =
          member.role != null && String(member.role).trim() !== ''
            ? String(member.role).trim()
            : '';
        return { role: this.normalizeOwnershipRole(rawRole), id: idNum };
      }
    }

    return {
      role: '',
      id: Number.isFinite(idNum) ? idNum : 0,
    };
  }

  normalizeDepartmentIds(value: number[] | number | string | null | undefined): number[] {
    if (Array.isArray(value)) {
      return value.filter(id => Number.isFinite(+id)).map(id => +id);
    }

    if (value === null || value === undefined || value === '') {
      return [];
    }

    return Number.isFinite(+value) ? [+value] : [];
  }

  departmentIdsFromDataSource(dataSource: any): number[] {
    if (Array.isArray(dataSource?.departments)) {
      return dataSource.departments
        .map((d: { id?: number } | number) =>
          typeof d === 'object' && d != null ? d.id : d
        )
        .filter(id => Number.isFinite(+id))
        .map(id => +id);
    }

    if (Array.isArray(dataSource?.departmentIds)) {
      return this.normalizeDepartmentIds(dataSource.departmentIds);
    }

    if (dataSource?.department?.id) {
      return [+dataSource.department.id];
    }

    return [];
  }

  /** Departments the data source is shared with (PUT share-departments sync list). */
  sharedDepartmentIdsFromDataSource(dataSource: unknown): number[] {
    if (!dataSource || typeof dataSource !== 'object') {
      return [];
    }

    const source = dataSource as Record<string, unknown>;
    const fromList = (value: unknown): number[] => {
      if (!Array.isArray(value)) {
        return [];
      }
      return value
        .map((entry: { id?: number } | number) =>
          typeof entry === 'object' && entry != null ? entry.id : entry
        )
        .filter(id => Number.isFinite(+id))
        .map(id => +id);
    };

    const fromSharedDepartments = fromList(source['sharedDepartments']);
    if (fromSharedDepartments.length) {
      return fromSharedDepartments;
    }

    const fromSharedDepartmentIds = this.normalizeDepartmentIds(
      source['sharedDepartmentIds'] as number[] | number | null | undefined
    );
    if (fromSharedDepartmentIds.length) {
      return fromSharedDepartmentIds;
    }

    const fromSharedWith = fromList(
      (source['sharedWith'] as { departments?: unknown[] } | undefined)?.departments
    );
    if (fromSharedWith.length) {
      return fromSharedWith;
    }

    // Legacy boolean: isPublic=true meant all assigned departments were shared.
    if (source['isPublic'] === true) {
      return this.departmentIdsFromDataSource(source);
    }

    return [];
  }

  ownerDisplayName(dataSource: any, lang: string = 'en'): string {
    const ownedBy = dataSource?.ownedBy as { name?: Record<string, string> | string } | undefined;
    return typeof ownedBy?.name === 'string'
      ? ownedBy.name
      : (ownedBy?.name?.[lang] ?? '');
  }

  departmentDisplayNames(
    dataSource: any,
    lang: string = 'en',
    includeType = true,
    includeOwner = true
  ): string {
    const departments = Array.isArray(dataSource?.departments)
      ? dataSource.departments
      : dataSource?.department
        ? [dataSource.department]
        : [];

    const ownerName = this.ownerDisplayName(dataSource, lang);

    const typeLabel = includeType
      ? this.formatDataSourceTypeLabel(dataSource?.type)
      : '';

    const names = departments
      .map((d: { name?: Record<string, string> | string }) => {
        const departmentName =
          typeof d?.name === 'string' ? d.name : (d?.name?.[lang] ?? '');
        if (!departmentName) {
          return '';
        }
        return typeLabel ? `${departmentName} (${typeLabel})` : departmentName;
      })
      .filter((name: string) => !!name);

    if (!names.length) {
      return '';
    }

    const departmentPart = names.join(', ');
    return includeOwner && ownerName ? `${departmentPart} - (${ownerName})` : departmentPart;
  }

  private formatDataSourceTypeLabel(type: string | null | undefined): string {
    if (!type) {
      return '';
    }
    return type
      .split(/\s+/)
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /** @param includeOwnership When true, Add/Edit payloads include `ownership`. */
  buildPayload(
    form: FormGroup,
    membersList?: any[],
    includeOwnership = false
  ): Record<string, unknown> {
    const joinsArray = form.get('joins') as FormArray;
    const joins = (joinsArray?.value ?? [])
      .map((j: string) => (j || '').trim())
      .filter((j: string) => j !== '');

    const type = form.get('type')?.value;
    const internalType = form.get('internalType')?.value;
    const externalType = form.get('externalType')?.value;

    const datatype = type === 'internal' ? internalType : externalType;

    const payload: Record<string, unknown> = {
      name: form.get('name')?.value ?? '',
      description: form.get('description')?.value ?? '',
      departmentIds: this.normalizeDepartmentIds(form.get('department')?.value),
      type,
      datatype,
      joins,
      // Cleared when the type no longer supports it, so switching away revokes it too.
      agentWritable:
        this.supportsAgentWriting(form) && !!form.get('agentWritable')?.value,
      // Linked external tables are managed through the external-tables endpoints,
      // not through this payload, so they are deliberately not sent here.
    };

    if (includeOwnership) {
      payload['ownership'] = this.buildOwnership(
        form.get('manager')?.value,
        membersList
      );
    }

    return payload;
  }
}
